import { z } from "zod/v4";

/**
 * Search params shared by the auth screens: where to send the user once they
 * are signed in.
 */
export const authSearchSchema = z.object({
  next: z.string().optional().catch(undefined),
  email: z.string().optional().catch(undefined),
});

export type AuthSearch = z.infer<typeof authSearchSchema>;

/**
 * Constrains a `next` target to this origin.
 *
 * Without this the parameter is an open redirect: an attacker could send
 * `/auth/sign-in?next=https://evil.example` and have our own sign-in page
 * bounce a freshly authenticated user onto their site. Anything not on this
 * origin — or not parseable at all — falls back to the app root.
 *
 * The same value is handed to better-auth as an OAuth `callbackURL`, where the
 * server independently checks it against `trustedOrigins`. This is the
 * client-side half of that pair, and it also covers the email/password path,
 * which never reaches that server-side check.
 */
export function safeNextUrl(next: string | undefined, origin = window.location.origin): string {
  if (!next) return "/";

  try {
    const url = new URL(next, origin);
    if (url.origin !== origin) return "/";
    return `${url.pathname}${url.search}`;
  } catch {
    return "/";
  }
}

/** Absolute form of {@link safeNextUrl}, for OAuth `callbackURL`. */
export function safeCallbackUrl(next: string | undefined): string {
  return `${window.location.origin}${safeNextUrl(next)}`;
}
